import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, UtensilsCrossed, Sparkles, Dumbbell } from "lucide-react";

// Enhanced 3D Service Card Component
const FeatureCard = ({ icon: Icon, title, description, index }) => {
  return (
    <motion.div
      initial={{
        opacity: 0,
        y: 20,
        rotateY: -5,
      }}
      animate={{
        opacity: 1,
        y: 0,
        rotateY: 0,
        transition: {
          duration: 0.6,
          delay: index * 0.15,
        },
      }}
      whileHover={{
        y: -10,
        rotateY: 5,
        scale: 1.02,
        transition: { duration: 0.3 },
      }}
      className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm p-8 rounded-lg shadow-xl border border-white/10 transform transition-all duration-300 hover:shadow-amber-500/20 group relative overflow-hidden"
      style={{
        transformStyle: "preserve-3d",
        perspective: "1000px",
      }}
    >
      {/* Background glow effect */}
      <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/0 to-amber-500/0 group-hover:from-amber-500/5 group-hover:to-amber-500/15 transition-all duration-500"></div>

      {/* Premium diagonal line accent */}
      <div className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-br from-amber-400/30 to-amber-600/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

      <div className="relative z-10">
        <div className="mb-6 text-amber-400 flex justify-center">
          <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20 transform transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110">
            <Icon size={28} strokeWidth={1.5} />
          </div>
        </div>
        <h4 className="text-xl font-medium mb-3 text-center">{title}</h4>
        <p className="text-slate-300 text-center">{description}</p>
      </div>

      {/* 3D bottom edge effect */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400/0 via-amber-400/30 to-amber-400/0 transform translate-y-px opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
    </motion.div>
  );
};

// Enhanced Feature Cards Component
const FeatureCards = () => {
  const features = [
    {
      icon: Building2,
      title: "Premium Accommodations",
      description:
        "Luxurious rooms and suites designed for ultimate comfort and relaxation.",
    },
    {
      icon: UtensilsCrossed,
      title: "Fine Dining",
      description:
        "Exquisite culinary experiences featuring local and international cuisines.",
    },
    {
      icon: Sparkles,
      title: "Spa Sanctuary",
      description:
        "Rejuvenating treatments and therapies to soothe your body and mind.",
    },
    {
      icon: Dumbbell,
      title: "Fitness Center",
      description:
        "State-of-the-art equipment and personal training to maintain your wellness routine.",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
      {features.map((feature, index) => (
        <FeatureCard
          key={index}
          icon={feature.icon}
          title={feature.title}
          description={feature.description}
          index={index}
        />
      ))}
    </div>
  );
};

const GulmoharGrand = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Enhanced animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 1.2 } },
  };

  const slideUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  const slideRight = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
  };

  const slideLeft = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
  };

  const staggered = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Navigation items
  const navItems = [
    "Home",
    "Rooms",
    "Dining",
    "Spa & Wellness",
    "Experiences",
    "Gallery",
    "Contact",
  ];

  return (
    <div className="font-serif bg-slate-50 text-slate-800 overflow-x-hidden">
      {/* Enhanced Welcome Section with Integrated CMD Quote */}
      <section className="py-12 md:py-32 px-6 md:px-16 lg:px-24 bg-white relative">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-amber-50"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-amber-50"></div>
        <div className="absolute top-1/4 right-1/4 w-16 h-16 rounded-full bg-amber-100/50 blur-xl"></div>
        <div className="absolute bottom-1/3 left-1/4 w-24 h-24 rounded-full bg-amber-100/50 blur-xl"></div>

        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggered}
            className="grid md:grid-cols-2 gap-16 items-start"
          >
            <motion.div variants={slideRight} className="relative z-10">
              <h3 className="text-3xl md:text-5xl font-light mb-8 tracking-wide leading-tight">
                THE GULMOHAR <br />
                <span className="text-amber-600">GRAND</span>
              </h3>
              <div className="w-16 h-1 bg-amber-500 mb-8 relative">
                <div className="absolute -left-3 top-0 w-3 h-1 bg-amber-300"></div>
                <div className="absolute -right-3 top-0 w-3 h-1 bg-amber-300"></div>
              </div>
              <p className="text-slate-600 mb-8 leading-relaxed text-lg">
                The Gulmohar Grand is a five-star luxury hotel, where we
                offer an array of opportunities to our guests to gather,
                connect, and savour the moment. Redefining contemporary
                hospitality for guests, The Gulmohar Grand for
                its name and renowned for the top-notch amenities it
                offers—attracts individuals visiting pilgrims in <a 
  href="https://devbhoomigroup.in" 
  target="_blank" 
  rel="noopener noreferrer" 
  className=" hover:underline"
>
  Dev Bhoomi Group Companies
</a>
,
                Himachal Pradesh.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                The Gulmohar Grand is all about modern conveniences,
                specially curated to make your stay one-of-a-kind. It provides a
                new dimension of luxury with just the right ingredients for a
                perfect stay – lavish accommodation, plush dining, a
                revitalizing spa, and an energizing fitness centre.
              </p>
              <p className="text-amber-600 italic font-medium">
                "With an aim to redefine conventional hospitality, we strive to
                offer an unforgettable experience to travelers."
              </p>
            </motion.div>

            <motion.div
              variants={slideRight}
              className="relative z-10 h-full min-h-56"
            >
              <div className="relative">
                <div className="absolute -top-5 -left-5 w-full h-full border-2 border-amber-500 z-0"></div>
                <motion.div
                  className="w-full overflow-hidden shadow-2xl z-10 relative"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.5 }}
                >
                  <img
                    src="https://ik.imagekit.io/tdlebsr5e/DJI_0230.JPG?updatedAt=1747388349261"
                    alt="Gulmohar Grand Exterior"
                    className="w-full h-full object-cover object-center transform hover:scale-105 transition-transform duration-700"
                  />

                  {/* Premium overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500"></div>
                </motion.div>

                {/* Decorative elements */}
                <div className="absolute -bottom-4 -right-4 w-16 h-16 bg-amber-100 rounded-full"></div>
                <div className="absolute top-1/2 -right-8 w-3 h-12 bg-amber-500"></div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* REDESIGNED: CMD Quote Section - Made smaller and more elegant */}
      <section className="py-16 px-6 md:px-16 lg:px-24 bg-gradient-to-r from-amber-50 to-slate-100 relative">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="mt-12 bg-gradient-to-r from-amber-50 to-slate-50 p-6 border-l-4 border-amber-500 shadow-lg relative overflow-hidden"
          >
            {/* Decorative elements */}
            <div className="absolute -top-6 -right-6 w-16 h-16 bg-amber-100 rounded-full opacity-60"></div>
            <div className="absolute -bottom-6 -left-6 w-12 h-12 bg-amber-200 rounded-full opacity-40"></div>

            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-50 to-amber-200 rounded-full overflow-hidden flex items-center justify-center border-2 border-amber-300 shadow-inner relative">
                  <span className="text-amber-600 font-semibold text-xl relative z-10">
                    RV
                  </span>
                  <div className="absolute inset-0 bg-amber-100 opacity-50 mix-blend-overlay"></div>
                </div>
              </div>

              <div className="flex-grow relative">
                <div className="text-5xl font-serif text-amber-200 absolute -top-4 -left-2">
                  "
                </div>
                <div className="text-5xl font-serif text-amber-200 absolute -bottom-6 right-0">
                  "
                </div>

                <p className="text-slate-700 italic mb-4 text-base leading-relaxed pl-4">
                  The comfort we offer, accompanied by a plethora of services,
                  makes us a preferred choice for both business and leisure
                  trips. People from all around the world visit Himachal
                  Pradesh, and The Gulmohar Grand ensures they experience
                  our unique 'Y-Gen' lifestyle.
                </p>

                <div className="flex items-center pl-4">
                  <div className="w-8 h-0.5 bg-amber-500 mr-3"></div>
                  <div>
                    <p className="font-medium text-slate-800">
                      Rajinder Vashista
                    </p>
                    <p className="text-amber-600 text-sm">
                      Chairman & Managing Director
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Enhanced Features Section with Premium 3D Effect Cards */}
      <section className="py-24 md:py-32 bg-slate-900 px-6 md:px-16 lg:px-24 relative text-white">
        <div className="absolute inset-0 bg-[url('')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900 to-slate-900/90"></div>

        {/* Premium geometric accents */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>
        <div className="absolute top-24 right-24 w-32 h-32 border border-amber-500/10 rounded-full"></div>
        <div className="absolute bottom-24 left-24 w-48 h-48 border border-amber-500/10 rounded-full"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center mb-20"
          >
            <motion.span
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-block px-6 py-2 bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-400/30 text-amber-400 text-xs tracking-widest uppercase mb-6 rounded-full shadow-lg shadow-amber-900/20"
            >
              Excellence in Every Detail
            </motion.span>

            <h3 className="text-4xl md:text-5xl font-light mb-6 tracking-wide">
              LUXURY AMENITIES
            </h3>

            <div className="relative w-24 h-1 bg-gradient-to-r from-amber-400/70 via-amber-500 to-amber-400/70 mx-auto mb-8">
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-12 h-px bg-amber-300/50"></div>
              <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-12 h-px bg-amber-300/50"></div>
            </div>

            <p className="text-slate-300 max-w-2xl mx-auto text-lg">
              Experience the epitome of luxury with our carefully curated
              selection of amenities and services.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggered}
          >
            <FeatureCards />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GulmoharGrand;
